(() => {
  const productSearchCache = new Map();
  const productSearchLimit = 8;
  const productSearchMinimumCharacters = 2;
  const productSearchDebounce = 220;

  const updateProductSummary = (root, input) => {
    const summary = root.querySelector('[data-quote-product-name]');
    if (!summary) return;
    summary.textContent = input.value.trim() || root.dataset.emptyProduct || '';
  };

  const normalizeProductQuery = (value) =>
    Array.from(value.trim().replace(/\s+/g, ' ')).slice(0, 100).join('');

  const createProductAutocomplete = (root, input) => {
    const combobox = root.querySelector('[data-quote-product-combobox]');
    const results = root.querySelector('[data-quote-product-results]');
    const status = root.querySelector('[data-quote-product-search-status]');
    const searchUrl = root.dataset.productSearchUrl;

    if (!combobox || !results || !status || !searchUrl) return null;

    let activeIndex = -1;
    let abortController = null;
    let currentQuery = '';
    let debounceTimer = null;
    let destroyed = false;
    let requestSequence = 0;

    const getOptions = () =>
      Array.from(results.querySelectorAll('[data-quote-product-option]'));

    const getQuery = () => normalizeProductQuery(input.value);

    const hasMinimumCharacters = (query) =>
      query.replace(/\s/g, '').length >= productSearchMinimumCharacters;

    const cacheKey = (query) =>
      `${document.documentElement.lang || 'en'}:${query.toLocaleLowerCase()}`;

    const setStatus = (message = '') => {
      status.textContent = message;
    };

    const clearSelection = () => {
      getOptions().forEach((option) => option.setAttribute('aria-selected', 'false'));
      activeIndex = -1;
      input.removeAttribute('aria-activedescendant');
    };

    const close = ({ clearStatus = false } = {}) => {
      results.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      clearSelection();
      if (clearStatus) setStatus();
    };

    const open = () => {
      if (getOptions().length === 0) return;
      results.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    };

    const cancelPendingSearch = () => {
      window.clearTimeout(debounceTimer);
      debounceTimer = null;
      requestSequence += 1;
      abortController?.abort();
      abortController = null;
      results.setAttribute('aria-busy', 'false');
    };

    const buildOptions = (titles) => {
      const fragment = document.createDocumentFragment();

      titles.forEach((title, index) => {
        const option = document.createElement('li');
        option.className = 'quote-request__product-option';
        option.id = `${results.id}-option-${index + 1}`;
        option.dataset.productTitle = title;
        option.dataset.quoteProductOption = '';
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', 'false');
        option.textContent = title;
        fragment.append(option);
      });

      results.replaceChildren(fragment);
    };

    const parseResults = (responseData) => {
      const products = responseData?.resources?.results?.products;
      if (!Array.isArray(products)) throw new Error('Invalid predictive search response');

      const seenTitles = new Set();
      const titles = [];

      products.forEach((product) => {
        const title = typeof product?.title === 'string' ? product.title.trim() : '';
        const normalizedTitle = title.toLocaleLowerCase();

        if (!title || seenTitles.has(normalizedTitle)) return;
        seenTitles.add(normalizedTitle);
        titles.push(title);
      });

      return {
        status:
          titles.length > 0
            ? root.dataset.productSearchResults || ''
            : root.dataset.productSearchEmpty || '',
        titles: titles.slice(0, productSearchLimit),
      };
    };

    const fetchResults = async (query, signal) => {
      const key = cacheKey(query);
      if (productSearchCache.has(key)) return productSearchCache.get(key);

      const url = new URL(searchUrl, window.location.origin);
      url.pathname = `${url.pathname.replace(/\/$/, '')}.json`;
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', String(productSearchLimit));
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set(
        'resources[options][fields]',
        'title,product_type,variants.title,variants.sku,vendor'
      );
      url.searchParams.set('resources[options][unavailable_products]', 'last');

      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        signal,
      });

      if (!response.ok) throw new Error(`Predictive search failed with ${response.status}`);

      const result = parseResults(await response.json());
      productSearchCache.set(key, result);
      return result;
    };

    const renderResults = (result, query) => {
      buildOptions(result.titles);
      currentQuery = query;
      setStatus(result.status);

      if (result.titles.length > 0) {
        open();
      } else {
        close();
      }
    };

    const search = async (query) => {
      const sequence = ++requestSequence;
      abortController?.abort();
      const controller = new AbortController();
      abortController = controller;
      results.setAttribute('aria-busy', 'true');
      setStatus(root.dataset.productSearchLoading || '');

      try {
        const result = await fetchResults(query, controller.signal);

        if (destroyed || sequence !== requestSequence || query !== getQuery()) return;
        renderResults(result, query);
      } catch (error) {
        if (error.name === 'AbortError' || destroyed || sequence !== requestSequence) return;

        results.replaceChildren();
        currentQuery = '';
        close();
        setStatus(
          [root.dataset.productSearchError, root.dataset.productSearchFallback]
            .filter(Boolean)
            .join('. ')
        );
      } finally {
        if (sequence === requestSequence) {
          results.setAttribute('aria-busy', 'false');
          if (abortController === controller) abortController = null;
        }
      }
    };

    const scheduleSearch = (query, delay = productSearchDebounce) => {
      cancelPendingSearch();

      if (!hasMinimumCharacters(query)) {
        results.replaceChildren();
        currentQuery = '';
        close({ clearStatus: true });
        return;
      }

      debounceTimer = window.setTimeout(() => {
        debounceTimer = null;
        search(query);
      }, delay);
    };

    const moveSelection = (direction) => {
      const options = getOptions();
      if (options.length === 0) return;

      if (activeIndex < 0) {
        activeIndex = direction > 0 ? 0 : options.length - 1;
      } else {
        activeIndex = (activeIndex + direction + options.length) % options.length;
      }

      options.forEach((option, index) => {
        option.setAttribute('aria-selected', index === activeIndex ? 'true' : 'false');
      });
      input.setAttribute('aria-activedescendant', options[activeIndex].id);
      options[activeIndex].scrollIntoView({ block: 'nearest' });
    };

    const setValue = (value) => {
      cancelPendingSearch();
      input.value = value;
      currentQuery = '';
      results.replaceChildren();
      close({ clearStatus: true });
      updateProductSummary(root, input);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    };

    const onInput = () => {
      const query = getQuery();
      results.replaceChildren();
      currentQuery = '';
      close({ clearStatus: true });
      scheduleSearch(query);
    };

    const onFocus = () => {
      const query = getQuery();
      if (!hasMinimumCharacters(query)) return;

      if (query === currentQuery && getOptions().length > 0) {
        open();
        return;
      }

      const cachedResult = productSearchCache.get(cacheKey(query));
      if (cachedResult) {
        renderResults(cachedResult, query);
        return;
      }

      scheduleSearch(query, 0);
    };

    const onKeydown = (event) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (getOptions().length === 0) return;
        event.preventDefault();
        open();
        moveSelection(event.key === 'ArrowDown' ? 1 : -1);
        return;
      }

      if (event.key === 'Enter' && activeIndex >= 0 && !results.hidden) {
        const selectedOption = getOptions()[activeIndex];
        if (!selectedOption) return;
        event.preventDefault();
        setValue(selectedOption.dataset.productTitle || selectedOption.textContent.trim());
        return;
      }

      if (
        event.key === 'Escape' &&
        (!results.hidden || abortController || debounceTimer !== null)
      ) {
        event.preventDefault();
        cancelPendingSearch();
        close({ clearStatus: true });
        return;
      }

      if (event.key === 'Tab') {
        cancelPendingSearch();
        close({ clearStatus: true });
      }
    };

    const onResultClick = (event) => {
      const option = event.target.closest('[data-quote-product-option]');
      if (!option || !results.contains(option)) return;

      event.preventDefault();
      input.focus();
      setValue(option.dataset.productTitle || option.textContent.trim());
    };

    const onFocusout = () => {
      window.setTimeout(() => {
        if (!combobox.contains(document.activeElement)) {
          cancelPendingSearch();
          close({ clearStatus: true });
        }
      });
    };

    const onDocumentPointerdown = (event) => {
      if (!combobox.contains(event.target)) {
        cancelPendingSearch();
        close({ clearStatus: true });
      }
    };

    input.addEventListener('input', onInput);
    input.addEventListener('focus', onFocus);
    input.addEventListener('keydown', onKeydown);
    results.addEventListener('click', onResultClick);
    combobox.addEventListener('focusout', onFocusout);
    document.addEventListener('pointerdown', onDocumentPointerdown);

    return {
      destroy() {
        destroyed = true;
        cancelPendingSearch();
        input.removeEventListener('input', onInput);
        input.removeEventListener('focus', onFocus);
        input.removeEventListener('keydown', onKeydown);
        results.removeEventListener('click', onResultClick);
        combobox.removeEventListener('focusout', onFocusout);
        document.removeEventListener('pointerdown', onDocumentPointerdown);
        close({ clearStatus: true });
      },
      setValue,
    };
  };

  const removeQuoteSuccessMarker = () => {
    const successUrl = new URL(window.location.href);
    if (!successUrl.searchParams.has('contact_posted')) return;
    successUrl.searchParams.delete('contact_posted');
    window.history.replaceState(window.history.state, '', successUrl.toString());
  };

  const scheduleQuoteSuccessMarkerCleanup = () => {
    const scheduleCleanup = () => window.setTimeout(removeQuoteSuccessMarker, 5000);
    if (document.readyState === 'complete') {
      scheduleCleanup();
      return;
    }
    window.addEventListener('load', scheduleCleanup, { once: true });
  };

  const publishQuoteSuccessToShopify = (successMessage) => {
    if (
      successMessage.dataset.quoteEventFired === 'true' ||
      !window.Shopify ||
      !window.Shopify.analytics ||
      typeof window.Shopify.analytics.publish !== 'function'
    ) {
      return false;
    }

    successMessage.dataset.quoteEventFired = 'true';

    try {
      window.Shopify.analytics.publish('jisc_scanners:contact_form_submitted', {
        form_type: 'quote_request',
      });
      return true;
    } catch {
      delete successMessage.dataset.quoteEventFired;
      return false;
    }
  };

  const publishQuoteSuccessFallback = (successMessage) => {
    if (successMessage.dataset.quoteEventFired === 'true') return;
    successMessage.dataset.quoteEventFired = 'true';

    const analyticsData = { form_type: 'quote_request' };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'jisc_scanners:contact_form_submitted',
      ...analyticsData,
    });
    document.dispatchEvent(
      new CustomEvent('jisc_scanners:contact_form_submitted', { detail: analyticsData })
    );
  };

  const initializeQuoteRequest = (root) => {
    if (root.dataset.quoteRequestReady === 'true') return;
    root.dataset.quoteRequestReady = 'true';

    const cleanupFunctions = [];
    const productInput = root.querySelector('[data-quote-product-input]');
    const unsureButton = root.querySelector('[data-quote-unsure]');
    const successfulRequest = root.querySelector('[data-quote-request-success]');
    const scanner = new URL(window.location.href).searchParams.get('scanner');
    const normalizedScanner = scanner
      ? Array.from(scanner.trim()).slice(0, 200).join('')
      : '';
    let productAutocomplete = null;

    if (productInput) {
      if (normalizedScanner && !productInput.value.trim()) {
        productInput.value = normalizedScanner;
      }

      const onProductInput = () => updateProductSummary(root, productInput);
      updateProductSummary(root, productInput);
      productInput.addEventListener('input', onProductInput);
      cleanupFunctions.push(() => productInput.removeEventListener('input', onProductInput));

      productAutocomplete = createProductAutocomplete(root, productInput);
      if (productAutocomplete) cleanupFunctions.push(() => productAutocomplete.destroy());
    } else if (normalizedScanner) {
      const productSummary = root.querySelector('[data-quote-product-name]');
      if (productSummary) productSummary.textContent = normalizedScanner;
    }

    if (productInput && unsureButton) {
      const onUnsureClick = () => {
        const unsureValue = unsureButton.dataset.unsureValue || '';
        productInput.focus();

        if (productAutocomplete) {
          productAutocomplete.setValue(unsureValue);
        } else {
          productInput.value = unsureValue;
          updateProductSummary(root, productInput);
        }
      };

      unsureButton.addEventListener('click', onUnsureClick);
      cleanupFunctions.push(() => unsureButton.removeEventListener('click', onUnsureClick));
    }

    if (successfulRequest) {
      scheduleQuoteSuccessMarkerCleanup();

      if (!publishQuoteSuccessToShopify(successfulRequest)) {
        const publishAfterLoad = () => {
          if (!publishQuoteSuccessToShopify(successfulRequest)) {
            publishQuoteSuccessFallback(successfulRequest);
          }
        };

        if (document.readyState === 'complete') {
          publishAfterLoad();
        } else {
          window.addEventListener('load', publishAfterLoad, { once: true });
        }
      }
    }

    root.quoteRequestCleanup = () => {
      cleanupFunctions.forEach((cleanup) => cleanup());
      cleanupFunctions.length = 0;
      delete root.quoteRequestCleanup;
      delete root.dataset.quoteRequestReady;
    };
  };

  const initializeAll = (scope = document) => {
    scope.querySelectorAll('[data-quote-request]').forEach(initializeQuoteRequest);
  };

  initializeAll();
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-quote-request]').forEach((root) => {
      root.quoteRequestCleanup?.();
    });
  });
})();
